import { useRef, useCallback, useEffect } from 'react';

const SCROLL_STEP = 280; // ~2 columns
const LEADING_CONTEXT_WEEKS = 2;

export function useGanttScroll() {
  const containerRef = useRef<HTMLDivElement>(null);
  const headerViewportRef = useRef<HTMLDivElement>(null);
  const headerTrackRef = useRef<HTMLDivElement>(null);
  const isPanning = useRef(false);
  const panStartX = useRef(0);
  const panStartScrollLeft = useRef(0);

  const scrollBy = useCallback((delta: number) => {
    containerRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
  }, []);

  const scrollToWeek = useCallback(
    (
      weekIndex: number,
      weekWidths: number[],
      leadingContextWeeks: number = LEADING_CONTEXT_WEEKS
    ) => {
      const container = containerRef.current;
      if (!container || weekIndex < 0 || weekIndex >= weekWidths.length) return;

      const startIndex = Math.max(0, weekIndex - leadingContextWeeks);
      let offset = 0;
      for (let i = 0; i < startIndex; i++) {
        offset += weekWidths[i];
      }

      container.scrollTo({ left: offset, behavior: 'instant' });
    },
    []
  );

  const scrollToStart = useCallback(() => {
    containerRef.current?.scrollTo({ left: 0, behavior: 'instant' });
  }, []);

  const scrollLeft = useCallback(() => {
    scrollBy(-SCROLL_STEP);
  }, [scrollBy]);
  const scrollRight = useCallback(() => {
    scrollBy(SCROLL_STEP);
  }, [scrollBy]);

  // The header strip is its own layer now, so the drag affordance has to be
  // applied to both: the grab cursor wherever the pointer went down, and no
  // text selection while it sweeps across the week labels on the way. The
  // viewport, not the track: the mouse handlers sit on the viewport, which is
  // the warning band taller, so a press in the band would otherwise pan the
  // year under a plain arrow. Both properties inherit, so the track and the
  // week cells inside it follow.
  const setDragStyles = useCallback((cursor: string, userSelect: string): void => {
    for (const element of [containerRef.current, headerViewportRef.current]) {
      if (!element) continue;
      element.style.cursor = cursor;
      element.style.userSelect = userSelect;
    }
  }, []);

  // One pan gesture, whichever kind of pointer started it: the press records
  // where it began and how far the scroller already was, and every move sets the
  // scroller from that origin. Mouse and touch share these three the way both
  // layers share setDragStyles — a second copy of the arithmetic is how the
  // strip and the rows would drift apart.
  const beginPan = useCallback(
    (clientX: number): void => {
      const container = containerRef.current;
      if (!container) return;
      isPanning.current = true;
      panStartX.current = clientX;
      panStartScrollLeft.current = container.scrollLeft;
      setDragStyles('grabbing', 'none');
    },
    [setDragStyles]
  );

  const continuePan = useCallback((clientX: number): void => {
    const container = containerRef.current;
    if (!isPanning.current || !container) return;
    container.scrollLeft = panStartScrollLeft.current - (clientX - panStartX.current);
  }, []);

  const endPan = useCallback((): void => {
    if (!isPanning.current) return;
    isPanning.current = false;
    setDragStyles('', '');
  }, [setDragStyles]);

  // Both layers' pan listeners are attached here rather than handed to the two
  // divs as JSX props, which is what closes Sonar's S6848
  // (jsx-a11y/no-static-element-interactions) at the cause: a div carrying
  // onMouseDown claims to be an interactive control, and the rule rightly wants
  // that claim backed by a role, focus and keys. There is no honest role to give
  // — region and group are non-interactive, so they trade this finding for three
  // others, and grid would promise rows and gridcells that do not exist below.
  // Panning is a pointer shortcut for the chevron buttons and the arrow keys, so
  // the truthful markup is a plain layout div and the plumbing belongs here.
  useEffect(() => {
    const rows = containerRef.current;
    const strip = headerViewportRef.current;
    if (!rows || !strip) return;

    const onMouseDown = (e: MouseEvent): void => {
      beginPan(e.clientX);
    };
    const onMouseMove = (e: MouseEvent): void => {
      continuePan(e.clientX);
    };
    const onTouchStart = (e: TouchEvent): void => {
      const touch = e.touches[0];
      if (touch) beginPan(touch.clientX);
    };
    const onTouchMove = (e: TouchEvent): void => {
      const touch = e.touches[0];
      if (touch) continuePan(touch.clientX);
    };

    for (const layer of [rows, strip]) {
      layer.addEventListener('mousedown', onMouseDown);
      layer.addEventListener('mousemove', onMouseMove);
      layer.addEventListener('mouseup', endPan);
      layer.addEventListener('mouseleave', endPan);
    }
    // Touch on the strip only. The rows layer is a real horizontal scroll
    // container, so a finger already pans it — the browser does it, measured —
    // and moving scrollLeft from here as well would send it twice as far per
    // swipe. The strip is overflow: clip, so nothing pans it natively; that is
    // the gap this closes. `touch-action: pan-x` there leaves the browser the
    // vertical gesture, so a finger on the pinned strip still scrolls the plan
    // list and nothing has to be preventDefault'ed.
    strip.addEventListener('touchstart', onTouchStart);
    strip.addEventListener('touchmove', onTouchMove);
    strip.addEventListener('touchend', endPan);
    strip.addEventListener('touchcancel', endPan);

    return () => {
      for (const layer of [rows, strip]) {
        layer.removeEventListener('mousedown', onMouseDown);
        layer.removeEventListener('mousemove', onMouseMove);
        layer.removeEventListener('mouseup', endPan);
        layer.removeEventListener('mouseleave', endPan);
      }
      strip.removeEventListener('touchstart', onTouchStart);
      strip.removeEventListener('touchmove', onTouchMove);
      strip.removeEventListener('touchend', endPan);
      strip.removeEventListener('touchcancel', endPan);
    };
  }, [beginPan, continuePan, endPan]);

  // The week-header strip sits outside the horizontal scroller so it can pin
  // vertically, so it has to be moved by hand. Every horizontal scroll path —
  // the chevron buttons, drag, arrow keys and scrollToWeek — goes through the
  // container's own scrolling, so a single scroll listener covers them all.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const syncHeaderTrack = (): void => {
      const track = headerTrackRef.current;
      if (!track) return;
      // translateX, not translate3d: forcing a compositing layer rasterizes this
      // text-dense strip separately from the rows, which reads softer at
      // fractional zoom. The sync is identical either way.
      track.style.transform = `translateX(${String(-container.scrollLeft)}px)`;
    };

    // The container may already be scrolled (the year view jumps to the current
    // week before this runs), so seed the offset rather than waiting for a scroll.
    syncHeaderTrack();
    container.addEventListener('scroll', syncHeaderTrack);
    return () => {
      container.removeEventListener('scroll', syncHeaderTrack);
    };
  }, []);

  // Arrow-key panning lives on the window and nowhere else: neither layer is
  // focusable, so there is no second listener to double-count a key press, and
  // the toolbar chevrons stay the discoverable, labelled way to pan.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip when a modal or drawer has locked page scroll
      if (document.body.style.overflow === 'hidden') return;

      const tag = (e.target as HTMLElement)?.tagName;
      if (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        (e.target as HTMLElement)?.isContentEditable
      ) {
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        scrollBy(-SCROLL_STEP);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        scrollBy(SCROLL_STEP);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [scrollBy]);

  return {
    containerRef,
    headerViewportRef,
    headerTrackRef,
    scrollLeft,
    scrollRight,
    scrollToWeek,
    scrollToStart
  };
}
